import { useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'

interface AuthProviderProps {
  children: ReactNode
}

/**
 * AuthProvider wraps the app to handle auth lifecycle.
 * - Initializes auth state on mount
 * - Listens for Supabase auth state changes
 * - Handles events: SIGNED_IN, SIGNED_OUT, PASSWORD_RECOVERY, TOKEN_REFRESHED
 * - Routes to appropriate screens based on auth state
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { initialize, handleAuthStateChange, isInitialized, user } = useAuthStore()

  // Initialize auth state on mount
  useEffect(() => {
    void initialize()
  }, [initialize])

  // Subscribe to auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      handleAuthStateChange(event, session)

      // Handle navigation based on auth events
      switch (event) {
        case 'SIGNED_IN':
          // If on a public auth page, redirect to home
          if (isPublicAuthRoute(location.pathname)) {
            void navigate('/', { replace: true })
          }
          break

        case 'SIGNED_OUT':
          // Redirect to landing page on sign out
          void navigate('/', { replace: true })
          break

        case 'PASSWORD_RECOVERY':
          // User clicked password reset link, navigate to reset password page
          void navigate('/reset-password', { replace: true })
          break

        default:
          break
      }
    })

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [handleAuthStateChange, navigate, location.pathname])

  // Handle initial routing once auth is initialized
  useEffect(() => {
    if (!isInitialized) return

    const currentPath = location.pathname

    console.log('[AuthProvider] Routing check:', {
      user: user?.email || 'null',
      currentPath,
      isPublicAuthRoute: isPublicAuthRoute(currentPath),
    })

    // If user is authenticated and on a public auth page, redirect to home
    if (user && isPublicAuthRoute(currentPath)) {
      console.log('[AuthProvider] User authenticated on public page, redirecting to /')
      void navigate('/', { replace: true })
    }
  }, [isInitialized, user, location.pathname, navigate])

  return <>{children}</>
}

/**
 * Check if the current route is a public auth route
 */
function isPublicAuthRoute(pathname: string): boolean {
  // Note: /subscribe is NOT included here because authenticated users
  // without a subscription legitimately need to access that page.
  // SubscribePage handles its own redirect when user has access.
  const publicAuthRoutes = [
    '/signup',
    '/signup/verify',
    '/signup/confirm',
    '/login',
    '/forgot-password',
  ]
  return publicAuthRoutes.includes(pathname)
}
